import styled from 'styled-components';
import { animated } from '@react-spring/web';

const libraryBg = require('../../assets/library.jpg');
const logoNoPadding = require('../../assets/logo-no-padding.png');

export const ContainerHero = styled(animated.div)`
  max-width: ${({ theme }) => theme.layout.pageMaxWidth};
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 1rem;
  }
`;

export const LogoImage = styled.img`
  max-width: 178px;
`;

export const StyledButtonLink = styled.a<{ $outline?: boolean; $isExcelCTA?: boolean }>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  border-radius: ${({ theme }) => theme.radii.sm};
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
  font-family: inherit;
  font-size: inherit;

  background: ${({ theme, $outline }) => $outline ? theme.colors.yellowAccent : '' };
  color: ${({ theme, $outline }) => $outline ? theme.colors.yellowAccent : theme.colors.textDark};
  border: 2px solid ${({ theme }) => theme.colors.yellowAccent};

  ${({ theme, $outline }) => $outline && `
    &:hover {
      background: ${theme.colors.yellowAccent};
      color: ${theme.colors.textDark};
    }
  `}

  ${({ theme, $isExcelCTA }) => $isExcelCTA && `
    min-width: 120px;
    background-color: ${theme.colors.darkPurple};
    color: ${theme.colors.textLight};
    font-weight: ${theme.fontWeights.normal};
    border-color: ${theme.colors.darkPurple};

    &:hover {
      background-color: ${theme.colors.backgroundLight};
      color: ${theme.colors.darkPurple};
      border-color: ${theme.colors.backgroundLight};
    }
  `}
`;

export const SectionTitleBase = styled(animated.h2)`
  font-family: ${({ theme }) => theme.fonts.roboto};
  font-size: ${({ theme }) => theme.fontSizes.xxxl};
  text-transform: uppercase;
  line-height: 1.1;
  letter-spacing: 5px;
  text-rendering: optimizeLegibility;
  display: inline-block;
  max-width: 800px;
  margin: ${({ theme }) => theme.spacing.lg} auto ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

export const StrokedTextH2 = styled(SectionTitleBase)`
  color: transparent;
  -webkit-text-stroke: 5px ${({ theme }) => theme.colors.darkPurple};
  text-stroke: 5px ${({ theme }) => theme.colors.darkPurple};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 3.5rem;
    -webkit-text-stroke-width: 2.5px;
    text-stroke-width: 2.5px;
    letter-spacing: 1px;
  }
`;

export const SiteHeader = styled(animated.header)`
  background: ${({ theme }) => theme.colors.darkPurple};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  height: 175px;

  ${ContainerHero} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }
`;

export const LogoH1 = styled.h1`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  letter-spacing: 2px;
  margin: 0;
  a {
    text-decoration: none;
    display: inline-block;
  }
`;

export const MobileMenuToggle = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  position: relative;
  padding: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  span {
    display: block;
    width: 24px;
    height: 24px;
    position: relative;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 24px;
      height: 3px;
      background: ${({ theme }) => theme.colors.yellowAccent};
      transition: transform 0.3s ease;
    }
    
    &::before {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    
    &::after {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(90deg);
    }
    
    ${({ $isOpen }) => $isOpen && `
      &::before {
        transform: translate(-50%, -50%) rotate(45deg);
      }
      
      &::after {
        transform: translate(-50%, -50%) rotate(45deg);
      }
    `}
  }
`;

export const MainNav = styled.nav<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  a {
    color: ${({ theme }) => theme.colors.textLight};
    text-decoration: none;
    margin-left: 1.5rem;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    &:first-child {
        margin-left: 0;
    }
  }
    ${StyledButtonLink} {
      border-radius: 16px;
      color: ${({ theme }) => theme.colors.black};
    }
   @media (max-width: ${({ theme }) => theme.breakpoints.md}) { 
    display: ${({ $isOpen }) => $isOpen ? 'flex' : 'none'};
    position: fixed;
    flex-direction: column;
    gap: 1.5rem;
    top: 175px; 
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.darkPurple};
    padding: 0.5rem 2rem 1rem;
    z-index: 999;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    a {
      margin-left: 0;
      padding: 0.75rem 0;
      width: 100%;
      box-sizing: border-box;
      font-size: ${({ theme }) => theme.fontSizes.md};
    }
    ${StyledButtonLink} {
      padding: 0.75rem 1.5rem;
      margin: 0.5rem 0;
    }
  }
`;

export const HeroSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl} 0;
  background: ${({ theme }) => theme.colors.backgroundLight};
  padding-top: 120px;
  min-height: calc(80vh - 100px);
`;

export const HeroTitle = styled(StrokedTextH2)``;

export const HeroSubtitle = styled(animated.p)`
  font-size: ${({ theme }) => theme.fontSizes.md};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textDark};
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

export const WhyJoinSection = styled.section`
  background: ${({ theme }) => theme.colors.backgroundGrey};
  padding: ${({ theme }) => theme.spacing.xl} 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 60vh;
`;

export const WhyJoinGrid = styled(animated.div)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.lg};
  justify-content: center;
  max-width: 1000px;
  margin: 0 auto;
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

export const WhyJoinColumn = styled(animated.div)`
  flex: 1 1 280px;
`;

export const WhyJoinList = styled(animated.ul)`
  list-style: none;
  padding: 0;
`;

export const WhyJoinHeading = styled(animated.h4)`
  font-family: ${({ theme }) => theme.fonts.roboto};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  text-transform: uppercase;
  color: transparent;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  letter-spacing: 5px;
  -webkit-text-stroke: 2px ${({ theme }) => theme.colors.darkPurple};
  text-stroke: 2px ${({ theme }) => theme.colors.darkPurple};
  text-rendering: optimizeLegibility;
`;

export const WhyJoinItem = styled(animated.li)`
  background: ${({ theme }) => theme.colors.darkPurple};
  color: ${({ theme }) => theme.colors.textLight};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.radii.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes.md};
  img {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    width: 45px;
    height: 45px;
  }
`;

export const HowItWorksSection = styled.section`
  background: ${({ theme }) => theme.colors.darkPurple};
  color: ${({ theme }) => theme.colors.textLight};
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 65vh;
  
  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.sm};
  }
`;

export const HowItWorksTitle = styled(SectionTitleBase)`
  font-family: ${({ theme }) => theme.fonts.heading};
  color: transparent;
  -webkit-text-stroke: 2px ${({ theme }) => theme.colors.textLight};
  text-stroke: 2px ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const HowItWorksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4rem;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 1024px) {
    gap: 3rem;
    max-width: 900px;
  }
  
  @media (max-width: 768px) {
    gap: 2.5rem;
    max-width: 700px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    max-width: 500px;
  }
`;

export const HowItWorksColumn = styled.div`
  min-width: 0;
`;

export const HowItWorksStepsList = styled.ol`
  list-style: none;
  counter-reset: step-counter;
  text-align: left;
  padding: 0;
  margin: 0;
`;

export const AnimatedStyledListItem = styled(animated.li)`
  position: relative;
  padding: 1.25rem 1.25rem 1.25rem 3.5rem;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: rgba(255,255,255,0.9);
  border-bottom: 2px solid rgba(255,255,255,0.2);
  cursor: pointer;
  box-sizing: border-box;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  line-height: 1.6;
  transition: all 0.3s ease;

  &::before {
    content: counter(step-counter, decimal-leading-zero);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0.75rem;
    font-size: ${({ theme }) => theme.fontSizes.xl};
    font-weight: bold;
    color: rgba(255,255,255,0.9);
  }
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
  
  &:hover {
    background: rgba(255,255,255,0.05);
    padding-left: 4rem;
  }
  
  counter-increment: step-counter;
  
  @media (max-width: 640px) {
    padding: 1rem 1rem 1rem 3rem;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    
    &::before {
      font-size: ${({ theme }) => theme.fontSizes.lg};
      left: 0.5rem;
    }
  }
`;

export const GenericAnimatedH4 = styled(animated.h4)`
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: ${({ theme }) => theme.fontSizes.xl};
    text-transform: uppercase;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    letter-spacing: 3px;
    font-weight: ${({ theme }) => theme.fontWeights.normal};
`;

export const ExcelCTASection = styled(animated.section)`
  position: relative;
  min-height: 70vh;
  background: url(${libraryBg}) center/cover no-repeat fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textLight};
  text-align: center;
`;

export const ExcelCTAOverlay = styled(animated.div)`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
`;

export const ExcelCTAContent = styled(animated.div)`
  position: relative;
  z-index: 1;
  max-width: 640px;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

export const ExcelCTATitle = styled(animated.h2)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.xxxl};
  text-transform: uppercase;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  letter-spacing: 5px;
  -webkit-text-stroke: 1px ${({ theme }) => theme.colors.textLight};
  text-stroke: 1px ${({ theme }) => theme.colors.textLight};
  text-rendering: optimizeLegibility;
   @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 3rem;
  }
`;

export const ExcelCTAText = styled(animated.p)`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: 1.5;
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 0.9rem;
  }
`;

export const ExcelCTAButtons = styled(animated.div)`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
    ${StyledButtonLink} {
      width: 100%;
      box-sizing: border-box;

    }
  }
`;

export const SiteFooter = styled.footer`
  background: ${({ theme }) => theme.colors.backgroundLight};
  padding: ${({ theme }) => theme.spacing.xl} 0;
  text-align: center;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.lg} 0;
  }
`;

export const FooterText = styled(StrokedTextH2).attrs({ as: 'p' })`
  font-size: 5rem;
  color: ${({ theme }) => theme.colors.white};
  -webkit-text-stroke: 3px ${({ theme }) => theme.colors.darkPurple};
  text-stroke: 3px ${({ theme }) => theme.colors.darkPurple};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  box-sizing: border-box;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 4rem;
    -webkit-text-stroke-width: 2.5px;
    text-stroke-width: 2.5px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 2.5rem;
    -webkit-text-stroke-width: 2px;
    text-stroke-width: 2px;
    letter-spacing: 2px;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    -webkit-text-stroke-width: 1.5px;
    text-stroke-width: 1.5px;
    letter-spacing: 1px;
  }
`;

export const ContactLink = styled.a`
  color: ${({ theme }) => theme.colors.textDark};
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.colors.darkPurple};
  }
`;

export const ContactText = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.sm};
    margin-top: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.textDark};
`;